/**
 * ES Services index — hub for all Spanish service pages.
 * Doubles as a landing for "servicios dentales fort worth" queries.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';
import { ROUTE_MAP_EN_TO_ES } from '@/i18n/routeMap';

const SITE = 'https://fortworthdental.com';

const CATEGORIES = [
  {
    title: 'Servicios Generales',
    services: [
      { name: 'Limpieza Dental', esRoute: ROUTE_MAP_EN_TO_ES['/preventative-dentistry'] },
      { name: 'Empastes', esRoute: ROUTE_MAP_EN_TO_ES['/composite-fillings'] },
      { name: 'Coronas y Puentes', esRoute: ROUTE_MAP_EN_TO_ES['/crowns-bridges'] },
      { name: 'Dentaduras Postizas', esRoute: ROUTE_MAP_EN_TO_ES['/dentures'] },
      { name: 'Endodoncia', esRoute: ROUTE_MAP_EN_TO_ES['/root-canals'] },
    ],
  },
  {
    title: 'Odontología Cosmética',
    services: [
      { name: 'Blanqueamiento Dental', esRoute: ROUTE_MAP_EN_TO_ES['/teeth-whitening'] },
      { name: 'Carillas de Porcelana', esRoute: ROUTE_MAP_EN_TO_ES['/porcelain-veneers'] },
      { name: 'Carillas First Fit', esRoute: ROUTE_MAP_EN_TO_ES['/first-fit-veneers'] },
      { name: 'Odontología Cosmética', esRoute: ROUTE_MAP_EN_TO_ES['/cosmetic-dentistry'] },
    ],
  },
  {
    title: 'Implantes y Ortodoncia',
    services: [
      { name: 'Implantes Dentales', esRoute: ROUTE_MAP_EN_TO_ES['/dental-implants'] },
      { name: 'Invisalign', esRoute: ROUTE_MAP_EN_TO_ES['/invisalign'] },
      { name: 'Injerto de Hueso', esRoute: ROUTE_MAP_EN_TO_ES['/bone-grafting'] },
    ],
  },
  {
    title: 'Encías y Periodoncia',
    services: [
      { name: 'Enfermedad de las Encías', esRoute: ROUTE_MAP_EN_TO_ES['/gum-disease'] },
      { name: 'Limpieza Profunda', esRoute: ROUTE_MAP_EN_TO_ES['/scaling-root-planing'] },
      { name: 'LANAP Láser', esRoute: ROUTE_MAP_EN_TO_ES['/lanap'] },
    ],
  },
  {
    title: 'Atención Urgente',
    services: [
      { name: 'Emergencia Dental', esRoute: ROUTE_MAP_EN_TO_ES['/emergency-dentist'] },
      { name: 'Sedación Dental', esRoute: ROUTE_MAP_EN_TO_ES['/sedation'] },
      { name: 'Detección de Cáncer Oral', esRoute: ROUTE_MAP_EN_TO_ES['/oral-cancer-screening'] },
    ],
  },
];

const SpanishServicesIndex = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  return (
  <>
    <EsSEO
      title="Servicios Dentales en Fort Worth | Fort Worth Dental"
      description="Limpiezas, blanqueamiento, frenos, implantes, endodoncia y más. Servicios dentales completos para toda la familia en Fort Worth, TX."
      canonical="/es/servicios"
      hreflangEn="/"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Servicios Dentales',
        inLanguage: 'es-US',
        url: `${SITE}/es/servicios`,
        about: { '@type': 'Dentist', name: 'Fort Worth Dental' },
      }}
    />

    <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Servicios Dentales</h1>
        <p className="text-lg text-teal-50 max-w-3xl">
          Atención dental completa para toda tu familia — desde limpiezas preventivas hasta transformaciones cosméticas y cirugía avanzada de implantes. Todo bajo un mismo techo.
        </p>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        {CATEGORIES.map((cat) => (
          <div key={cat.title} className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b border-teal-200 pb-2">{cat.title}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.services.map((s) => (
                <Link
                  key={s.esRoute}
                  to={s.esRoute}
                  className="block bg-gray-50 border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-teal-400 transition-all"
                  data-testid={`es-services-link-${s.esRoute}`}
                >
                  <span className="font-semibold text-gray-800 group-hover:text-teal-700">{s.name}</span>
                  <span className="text-teal-600 float-right">→</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="py-12 bg-teal-50 border-y border-teal-100">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">¿No encuentras lo que buscas?</h2>
        <p className="text-gray-700 mb-5">
          Llámanos al <a href={telLink} className="text-teal-600 font-semibold">{displayNumber}</a> y te ayudamos en español. Atendemos sin cita previa para emergencias.
        </p>
        <Link
          to="/es/contacto"
          className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-md"
        >
          Contáctanos
        </Link>
      </div>
    </section>
  </>
  );
};

export default SpanishServicesIndex;
